import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useState } from 'react';

import type { Insight, InsightListResponse } from './types';

import { sortInsights } from '~/components/Claim/Insights/InsightsHelper.tsx';
import { get } from '~/utils/request';

interface InsightsContextType {
  insights: Insight[];
  isInsightsLoading: boolean;
  insightsError: string | null;
  fetchInsights: (claimNumber: string) => Promise<void>;
}

const InsightsContext = createContext<InsightsContextType | undefined>(undefined);

export const useInsightsContext = () => {
  const ctx = useContext(InsightsContext);
  if (!ctx) throw new Error('useInsightsContext must be used within an InsightsProvider');
  return ctx;
};

interface InsightsProviderProps {
  children: ReactNode;
}

export const InsightsProvider: React.FC<InsightsProviderProps> = ({ children }) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  const fetchInsights = useCallback(async (claimNumber: string) => {
    setIsInsightsLoading(true);
    setInsightsError(null);
    try {
      const response = await get<InsightListResponse>({
        url: `/api/v1/insights/${claimNumber}`,
      });
      setInsights(sortInsights(response.insights));
    } catch (error) {
      setInsightsError('Failed to fetch claim insights. please try to refresh the page.');
      console.error('Error fetching insights:', error);
    } finally {
      setIsInsightsLoading(false);
    }
  }, []);

  return (
    <InsightsContext.Provider value={{ insights, isInsightsLoading, insightsError, fetchInsights }}>
      {children}
    </InsightsContext.Provider>
  );
};
